import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Loader2, ShieldAlert, ShieldCheck, Ban, Check, RefreshCw, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { adjudicateClaim } from "@/server/claim-admin.functions";
import { AdminPageShell } from "@/components/dashboard/AdminPageShell";
import { loadClaimsFn } from "./-dashboard.admin.claims.functions";

export const Route = createFileRoute("/dashboard/admin/claims")({
  head: () => ({ meta: [{ title: "Claim Review · Admin" }, { name: "robots", content: "noindex" }] }),
  component: ClaimReviewPage,
});

type Row = {
  id: string;
  name: string | null;
  practice: string | null;
  city: string | null;
  state: string | null;
  claimed_by: string | null;
  claim_status: string;
  risk_score: number;
  risk_flags: string[];
  phone_verified: boolean;
  plan: string;
  premium_subscription_status: string;
  auto_approved_at: string | null;
};

const PAGE_SIZE = 50;
export type Cursor = { auto_approved_at: string | null; id: string } | null;

function ClaimReviewPage() {
  const [tab, setTab] = useState<"all" | "flagged" | "auto_approved" | "restricted" | "rejected">("all");
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const adjudicate = useServerFn(adjudicateClaim);
  const [cursorStack, setCursorStack] = useState<Cursor[]>([null]);
  const [pageIndex, setPageIndex] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const reqIdRef = useRef(0);

  useEffect(() => { setCursorStack([null]); setPageIndex(0); }, [tab]);

  const load = useCallback(async () => {
    const myReq = ++reqIdRef.current;
    setLoading(true);
    try {
      const cursor = cursorStack[pageIndex] ?? null;
      // NOTE: no count:"exact" — cursor pagination on providers (6M+ rows).
      const data = await loadClaimsFn({
        data: { tab, cursor, limit: PAGE_SIZE + 1 },
      });
      if (myReq !== reqIdRef.current) return;
      const fetched = (data ?? []) as Row[];
      setHasMore(fetched.length > PAGE_SIZE);
      setRows(fetched.slice(0, PAGE_SIZE));
    } catch (e: any) {
      if (myReq !== reqIdRef.current) return;
      toast.error(e?.message ?? "Failed to load");
      setRows([]); setHasMore(false);
    } finally {
      if (myReq === reqIdRef.current) setLoading(false);
    }
  }, [tab, cursorStack, pageIndex]);

  useEffect(() => { load(); }, [load]);

  const act = async (providerId: string, action: "approve" | "restrict" | "reject") => {
    try {
      await adjudicate({ data: { providerId, action } });
      toast.success(`Claim ${action}d`);
      load();
    } catch (e: any) {
      toast.error(e?.message ?? "Action failed");
    }
  };

  const goNext = () => {
    if (!hasMore || rows.length === 0) return;
    const last = rows[rows.length - 1];
    const next: Cursor = { auto_approved_at: last.auto_approved_at, id: last.id };
    setCursorStack((s) => { const c = s.slice(0, pageIndex + 1); c.push(next); return c; });
    setPageIndex((p) => p + 1);
  };
  const goPrev = () => { if (pageIndex > 0) setPageIndex((p) => p - 1); };


  return (
    <AdminPageShell
      title="Claims"
      description="Auto-approved claims, flagged risks, and adjudication queue."
      primaryAction={{ label: "Refresh", icon: RefreshCw, onClick: load }}
    >
      <Tabs value={tab} onValueChange={(v) => setTab(v as any)}>
        <TabsList>
          <TabsTrigger value="all">All</TabsTrigger>
          <TabsTrigger value="flagged">Flagged</TabsTrigger>
          <TabsTrigger value="auto_approved">Auto-approved</TabsTrigger>
          <TabsTrigger value="restricted">Restricted</TabsTrigger>
          <TabsTrigger value="rejected">Rejected</TabsTrigger>
        </TabsList>
        <TabsContent value={tab} className="mt-4">
          {loading ? (
            <div className="flex justify-center p-12"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground"/></div>
          ) : rows.length === 0 ? (
            <div className="card-surface-flat rounded-md p-12 text-center text-muted-foreground text-sm">No claims in this tab.</div>
          ) : (
            <div className="card-surface rounded-md overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/30 text-xs uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="text-left px-4 py-3">Listing</th>
                    <th className="text-left px-4 py-3">Plan</th>
                    <th className="text-left px-4 py-3">Phone</th>
                    <th className="text-left px-4 py-3">Risk</th>
                    <th className="text-left px-4 py-3">Flags</th>
                    <th className="text-right px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="border-t border-border">
                      <td className="px-4 py-3">
                        <div className="font-medium">{r.practice ?? r.name}</div>
                        <div className="text-xs text-muted-foreground">{[r.city, r.state].filter(Boolean).join(", ")}</div>
                      </td>
                      <td className="px-4 py-3 text-xs">{r.plan}</td>
                      <td className="px-4 py-3">{r.phone_verified ? <ShieldCheck className="h-4 w-4 text-primary"/> : <span className="text-xs text-muted-foreground">unverified</span>}</td>
                      <td className="px-4 py-3 font-mono">{r.risk_score}</td>
                      <td className="px-4 py-3"><div className="flex flex-wrap gap-1">{r.risk_flags.map((f) => (<span key={f} className="text-[10px] uppercase tracking-wider bg-muted px-1.5 py-0.5 rounded-sm">{f}</span>))}</div></td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1 justify-end">
                          <Button size="sm" variant="outline" onClick={() => act(r.id, "approve")} title="Approve"><Check className="h-3 w-3"/></Button>
                          <Button size="sm" variant="outline" onClick={() => act(r.id, "restrict")} title="Restrict"><ShieldAlert className="h-3 w-3"/></Button>
                          <Button size="sm" variant="outline" onClick={() => act(r.id, "reject")} title="Reject"><Ban className="h-3 w-3"/></Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="flex items-center justify-between mt-3 text-xs text-muted-foreground">
            <div>Showing {rows.length === 0 ? 0 : pageIndex * PAGE_SIZE + 1}–{pageIndex * PAGE_SIZE + rows.length}{hasMore ? " · more available" : ""}</div>
            <div className="flex items-center gap-1">
              <Button size="sm" variant="outline" disabled={pageIndex === 0 || loading} onClick={goPrev}><ChevronLeft className="h-3 w-3" /></Button>
              <span className="px-2">Page {pageIndex + 1}</span>
              <Button size="sm" variant="outline" disabled={!hasMore || loading} onClick={goNext}><ChevronRight className="h-3 w-3" /></Button>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </AdminPageShell>
  );
}
